// src/components/SetupPage.jsx
import React from 'react';
import { Routes, Route, NavLink, Navigate, useParams } from 'react-router-dom';
import { SlPuzzle } from "react-icons/sl";

import TeachersPage from './TeachersPage';
import TeacherDetailPage from './TeacherDetailPage';
import GroupsPage from './GroupsPage';
import RoomsPage from './RoomsPage';
import RoomDetailPage from './RoomDetailPage';
import SubjectsPage from './SubjectsPage.tsx';
import GroupDetailPage from './GroupDetailPage';
import Card from './ui/Card';
import ClassSchedulerPage from './ClassSchedulerPage';
import SchoolSettingsPage from './SchoolSettingsPage';
import YearStructureEditor from './YearStructureEditor';

const TABS = [
    {
        id: 'school-settings',
        label: 'School Settings',
        path: '/setup/school-settings',
        tooltip: 'Configure your institution\'s basic information, contact details, and general settings'
    },
    {
        id: 'year-structure',
        label: 'Year Structure',
        path: '/setup/year-structure',
        tooltip: 'Define academic years, terms, periods, and holiday schedules'
    },
    {
        id: 'teachers',
        label: 'Teachers',
        path: '/setup/teachers',
        tooltip: 'Manage teaching staff, their qualifications, and subject assignments'
    },
    {
        id: 'groups',
        label: 'Groups',
        path: '/setup/groups',
        tooltip: 'Organize students into classes and groups, manage enrollment'
    },
    {
        id: 'rooms',
        label: 'Rooms',
        path: '/setup/rooms',
        tooltip: 'Set up classrooms, labs, and other facilities with capacity and equipment details'
    },
    {
        id: 'subjects',
        label: 'Subjects',
        path: '/setup/subjects',
        tooltip: 'Define courses, curriculum subjects, and academic requirements'
    },
    {
        id: 'class-scheduler',
        label: (
            <span className="flex items-center space-x-2">
                <SlPuzzle className="h-4 w-4" />
                <span>Class Scheduler</span>
            </span>
        ),
        path: '/setup/class-scheduler',
        tooltip: 'Create and manage class schedules, assign time slots and resources'
    },
];

// Accept user prop
function SetupPage({
    user,
    teachers,
    onAddTeacher,
    onUpdateTeacher,
    onDeleteTeacher,
    groups,
    onAddGroup,
    onUpdateGroup,
    onDeleteGroup,
    onAddStudentToGroup,
    onAddStudentsToGroupBatch,
    onRemoveStudentFromGroup,
    onAddNewClass,
    rooms,
    onAddRoom,
    onUpdateRoom,
    onDeleteRoom,
    subjects,
}) {

    const getNavLinkClass = ({ isActive }) => {
        return `px-4 py-2 rounded-t-md text-sm font-medium focus:outline-none transition-colors duration-150 ${isActive
            ? 'bg-white border border-gray-200 border-b-0 text-blue-600 font-semibold shadow-sm'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-transparent'
            }`;
    };

    return (
        <div className="p-4 md:p-6 lg:p-8 bg-gray-100 min-h-screen">
            {/* <h1 className="text-3xl font-bold text-gray-800 mb-6">Setup Management</h1> */}

            <Card className="mb-6 p-2 shadow-sm bg-white">
                <div className="flex space-x-1 border-b border-gray-200 pb-px overflow-x-auto"> {/* Added overflow-x-auto for smaller screens */}
                    {TABS.map(tab => (
                        <NavLink key={tab.id} to={tab.path} className={getNavLinkClass} title={tab.tooltip}>
                            {tab.label}
                        </NavLink>
                    ))}
                </div>
            </Card>

            <div className="mt-4">
                <Routes>
                    {/* School Settings Route */}
                    <Route
                        path="school-settings"
                        element={<SchoolSettingsPage user={user} />}
                    />

                    {/* Teachers List Route */}
                    <Route
                        path="teachers"
                        element={<TeachersPage teachers={teachers} onAddTeacher={onAddTeacher} />}
                    />
                    {/* Teacher Detail Route */}
                    <Route
                        path="teachers/:teacherId"
                        element={
                            <TeacherDetailPage
                                user={user}
                                onUpdateTeacher={onUpdateTeacher}
                                onDeleteTeacher={onDeleteTeacher}
                            />
                        }
                    />

                    {/* Groups List Route */}
                    <Route
                        path="groups"
                        element={<GroupsPage groups={groups} onAddGroup={onAddGroup} />}
                    />
                    {/* Group Detail Route */}
                    <Route
                        path="groups/:groupId"
                        element={
                            <GroupDetailPageWrapper
                                user={user}
                                groups={groups}
                                teachers={teachers}
                                subjects={subjects}
                                rooms={rooms}
                                onUpdateGroup={onUpdateGroup}
                                onDeleteGroup={onDeleteGroup}
                                onAddStudentToGroup={onAddStudentToGroup}
                                onAddStudentsToGroupBatch={onAddStudentsToGroupBatch}
                                onRemoveStudentFromGroup={onRemoveStudentFromGroup}
                                onAddNewClass={onAddNewClass}
                            />
                        }
                    />

                    {/* Rooms Route */}
                    <Route
                        path="rooms"
                        element={
                            <RoomsPage
                                rooms={rooms}
                                onAddRoom={onAddRoom}
                                onUpdateRoom={onUpdateRoom}
                                onDeleteRoom={onDeleteRoom}
                            />
                        }
                    />
                    {/* Room Detail Route */}
                    <Route
                        path="rooms/:roomId"
                        element={
                            <RoomDetailPage
                                user={user}
                                onUpdateRoom={onUpdateRoom}
                                onDeleteRoom={onDeleteRoom}
                            />
                        }
                    />

                    {/* Subjects Route */}
                    <Route path="subjects" element={<SubjectsPage />} />

                    {/* Year Structure Route */}
                    <Route
                        path="year-structure"
                        element={<YearStructureEditor user={user} />}
                    />

                    {/* Class Scheduler Route - MODIFIED TO PASS USER PROP */}
                    <Route
                        path="class-scheduler"
                        element={<ClassSchedulerPage user={user} />} // Pass user to ClassSchedulerPage
                    />

                    {/* Default route: navigate to the first tab or a preferred default */}
                    <Route index element={<Navigate to={TABS[0].path} replace />} />
                    <Route path="*" element={<Navigate to={TABS[0].path} replace />} />
                </Routes>
            </div>
        </div>
    );
}

function GroupDetailPageWrapper({
    user,
    groups,
    teachers,
    subjects,
    rooms,
    onUpdateGroup,
    onDeleteGroup,
    onAddStudentToGroup,
    onAddStudentsToGroupBatch,
    onRemoveStudentFromGroup,
    onAddNewClass
}) {
    const { groupId: groupIdFromUrl } = useParams();
    const numericGroupIdFromUrl = parseInt(groupIdFromUrl);

    console.log(`[GroupDetailPageWrapper] Rendering. Received groups array length: ${groups ? groups.length : 'undefined'}. Target URL groupId: ${groupIdFromUrl} (numeric: ${numericGroupIdFromUrl})`);

    if (groups && groups.length > 0) {
        console.log('[GroupDetailPageWrapper] Groups array contents (first 5 IDs):', groups.slice(0, 5).map(g => g.id));
    }

    const groupToPass = groups ? groups.find(g => g.id === numericGroupIdFromUrl) : undefined;

    console.log(`[GroupDetailPageWrapper] Group finding result - ID: ${numericGroupIdFromUrl}. Found group object:`, groupToPass ? { id: groupToPass.id, name: groupToPass.name, studentsCount: groupToPass.students?.length, prefsCount: groupToPass.preferences?.length } : 'NOT FOUND');

    if (groups && groups.length > 0 && !groupToPass && groupIdFromUrl) {
        console.warn(`[GroupDetailPageWrapper] Group with ID ${numericGroupIdFromUrl} was NOT FOUND in the provided groups list (length: ${groups.length}). URL ID: ${groupIdFromUrl}.`);
    }

    return (
        <GroupDetailPage
            user={user}
            group={groupToPass}
            groups={groups}
            teachers={teachers}
            subjects={subjects}
            rooms={rooms}
            onUpdateGroup={onUpdateGroup}
            onDeleteGroup={onDeleteGroup}
            onAddStudentToGroup={onAddStudentToGroup}
            onAddStudentsToGroupBatch={onAddStudentsToGroupBatch}
            onRemoveStudentFromGroup={onRemoveStudentFromGroup}
            onAddNewClass={onAddNewClass}
        />
    );
}

export default SetupPage;